import { Box, Card, CardContent, Typography } from "@mui/material"
import React, { useCallback, useContext, useState } from "react"
import { customPalette } from "../../../../../../../../../theme"
import { DeleteButton } from "../../../../../../../../components/buttons/DeleteButton/DeleteButton"
import {
  BOTTOM_NAVIGATION_HEIGHT,
  CustomBottomNavigation,
} from "../../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { BSCalculStatusChip } from "../../../../../../../../components/card/beem-shot/BSCalculStatusChip"
import { SuccessContext } from "../../../../../../../../components/layout/success-snackbar"
import { CalculStatusEnum } from "../../../../../../../../core/enum/calculStatusEnum"
import { useForm } from "../../../../../../../../core/hooks/form/use-form"
import { required } from "../../../../../../../../core/hooks/form/validation"
import { BSBuildingContext } from "../../../building/components/context/BSBuildingContext"
import { BSVariantControlPanelContext } from "../../../components/BSVariantControlPanelContext"
import { ControlPanelTabHeader } from "../../../components/ControlPanelTabHeader"
import {
  BSBuildingInfosForm,
  bsBuildingNameDescriptionDtoToForm,
  bsBuildingNameDescriptionFormToUpdateDto,
  IBSBuildingNameDescriptionForm,
} from "./components/BSBuildingInfosForm"
import { DeleteBuildingDialog } from "./components/DeleteBuildingDialog"

interface IProps {
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>
  isUpdating: boolean
}

export function BSBuildingInfosTab({ setSelectedTab, setIsUpdating, isUpdating }: Readonly<IProps>): React.JSX.Element {
  const openSuccessSnackbar = useContext(SuccessContext)
  const { selectedBSBuilding, updateBSBuildingInfos, updateCalculStatusState } = useContext(BSBuildingContext)
  const { controlPanelRefWidth } = useContext(BSVariantControlPanelContext)

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

  const formId = "update-bs-building-infos"

  const submit: (form: IBSBuildingNameDescriptionForm) => Promise<any> = useCallback(
    (form) => {
      if (selectedBSBuilding) {
        return updateBSBuildingInfos(bsBuildingNameDescriptionFormToUpdateDto(form, selectedBSBuilding.id)).then(() => {
          openSuccessSnackbar("Les informations du bâtiment ont été mises à jour.")
          setIsUpdating(false)
        })
      }
      return Promise.resolve()
    },
    [selectedBSBuilding, updateBSBuildingInfos, openSuccessSnackbar, setIsUpdating]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting } = useForm(
    selectedBSBuilding,
    bsBuildingNameDescriptionDtoToForm,
    [required("name")],
    submit,
    () => setIsUpdating(true)
  )

  const handleOpenDeleteDialog: () => void = useCallback(() => {
    setOpenDeleteDialog(true)
  }, [])

  return (
    <Box
      height={isUpdating ? `calc(100% - ${BOTTOM_NAVIGATION_HEIGHT} -  2vh)` : "100%"}
      px={5}
      pb={2}
      sx={{ overflow: "auto" }}>
      <ControlPanelTabHeader title="Informations" />
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", my: 2 }}>
        <DeleteButton label="Supprimer le bâtiment" onClick={() => handleOpenDeleteDialog()} />
      </Box>

      <Card
        sx={{
          backgroundColor: customPalette.white,
          borderRadius: 3,
          mb: 3,
          boxShadow: "2px 4px 20px 0px rgba(0, 0, 0, 0.05)",
        }}>
        <CardContent>
          <BSBuildingInfosForm
            formId={formId}
            form={form}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            errors={errors}
          />
          <Typography variant="body1" fontWeight={600} sx={{ color: customPalette.darkBlue2 }}>
            Status du calcul
          </Typography>
          {selectedBSBuilding && (
            <BSCalculStatusChip
              calculStatus={selectedBSBuilding.calculStatus}
              diffIc2022={selectedBSBuilding.diffIc2022}
              diffIc2025={selectedBSBuilding.diffIc2025}
              diffIc2028={selectedBSBuilding.diffIc2028}
              diffIc2031={selectedBSBuilding.diffIc2031}
              higherSeuilValide={selectedBSBuilding.higherSeuilValide}
            />
          )}
        </CardContent>
      </Card>

      {isUpdating && (
        <CustomBottomNavigation
          actionLabel="Enregistrer"
          formId={formId}
          isSubmitting={isSubmitting}
          additionalLeftPadding={controlPanelRefWidth}
        />
      )}
      <DeleteBuildingDialog setSelectedTab={setSelectedTab} open={openDeleteDialog} setOpen={setOpenDeleteDialog} />
    </Box>
  )
}
